import React, { useEffect, useState } from "react";
import {
  Col,
  Row,
  Select,
  Button,
  Input,
  Form,
  message,
  Space,
  // Tooltip, // Tooltip was imported but not used
  Card,
  Divider,
  InputNumber,
  Checkbox,
  Typography,
} from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  AUTH_TOKEN,
  REACT_APP_API_PAM_URL,
  REACT_APP_PAM_API,
  ORG_ID,
  DOMAIN_ID,
} from "../../../constants";
import { logOutUser } from "../../../common";
import ReactJoyride from "react-joyride";

const { Title } = Typography;

const AddPasswordPolicy = () => {
  const [serverHostName, setServerHostName] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [form] = Form.useForm();
  const [notification, setNotification] = message.useMessage();
  const [template, setTemplate] = useState([{ value: 0, label: "Default" }]);
  const [checkPolicyName, setCheckPolicyName] = useState([]);
  const [epmGroup, setEpmGroup] = useState([]);
  const [endpointLength, setEndpointLength] = useState(false);
  const [endpointGrpLength, setEndpointGrpLength] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const onBoardType = queryParams.get("type");
  const [isOnBoarding, setIsOnboarding] = useState(false);
  const [run, setRun] = useState(true);
  const [userTypeVal, setUserTypeVal] = useState("LDAP");
  const [enablePasswordComplexity, setEnablePasswordComplexity] = useState(true);

  const defaultSpecialCharsArray = "!@#$%^&*()_+-=[]{}|;:',.<>/?".split("");

  // Removed: const enableSpecialCharRules = Form.useWatch("enableSpecialCharRules", form);

  useEffect(() => {
    setIsOnboarding(onBoardType ? true : false);
  }, [onBoardType]);

  const startTour = () => {
    const tourStep = [
      {
        target: "#onboarding_step1",
        content: <span>Enter a unique policy name</span>,
        disableBeacon: true,
      },
      {
        target: "#onboarding_step2",
        content: <span>Select either multiple Endpoints or Endpoint Groups</span>,
        disableBeacon: true,
      },
      {
        target: "#onboarding_step3",
        content: <span>Select any Rotation Time</span>,
        disableBeacon: true,
      },
      {
        target: "#onboarding_step4",
        content: <span>Click to save credential rotation policy for password</span>,
        disableBeacon: true,
      },
    ];
    const handleTourCallback = ({ status }) => {
      if (status === "finished" || status === "skipped") setRun(false);
    };
    return (
      <ReactJoyride
        steps={tourStep}
        run={run}
        floaterProps={{ disableAnimation: true }}
        callback={handleTourCallback}
        continuous
        scrollToFirstStep
        disableScrollParentFix
        scrollOffset={100}
        showSkipButton
        showProgress
        hideCloseButton={true}
        styles={{
          buttonNext: { backgroundColor: "blue", color: "#ffffff" },
          buttonBack: { color: "#1677ff" },
        }}
      />
    );
  };

  useEffect(() => {
    fetchInstance();
    fetchEndpointGroup();
    fetchCredRotationPolicy();
  }, []);

  useEffect(() => {
    const initialValues = {
      userType: "LDAP",
      rotationTime: 1,
      template: 0,
      policyName: "",
      minLength: 8,
      minUppercase: 0,
      minLowercase: 0,
      minDigits: 0,
      minSpecialChars: 0,
      allowStartSpecial: false,
      allowEndSpecial: false,
      specialCharSet: defaultSpecialCharsArray,
      instanceId: [],
      instanceGroupId: [],
    };
    form.setFieldsValue(initialValues);
    setUserTypeVal("LDAP");
  }, [form]);

  const fetchEndpointGroup = async () => {
    let currentDomainId = localStorage.getItem("DomainId");
    let pageDetails = {
      domainId: currentDomainId,
      orgId: ORG_ID(),
      pageId: 1,
      pageSize: 100,
      token: AUTH_TOKEN(),
      search: "",
      filter: { filterBy: "", value: "" },
    };
    try {
      let res = await axios.post(
        `${REACT_APP_PAM_API}/instanceGroup/listEndpointGroup`,
        pageDetails,
        { headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true } }
      );
      setEpmGroup(
        res?.data?.groups.map((grp) => ({ value: grp.groupId, label: grp.groupName })) || []
      );
    } catch (err) {
      if (err?.response?.data?.message === "Invalid credentials" || err?.response?.status === 401) {
        notification.open({ type: "error", content: "Credentials are invalid. Logging out." });
        logOutUser();
      } else {
        notification.open({ type: "error", content: "Error fetching Endpoint Groups." });
      }
    }
  };

  const fetchInstance = () => {
    let payload = {
      domainId: DOMAIN_ID(),
      pageId: 1,
      pageSize: 100,
      search: "",
      filter: { filterBy: "", value: "" },
      token: AUTH_TOKEN(),
      orgId: ORG_ID(),
      MachineType: "endpoint",
    };
    axios
      .post(`${REACT_APP_PAM_API}/instances/list`, payload, {
        headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() },
      })
      .then((res) => {
        setServerHostName(
          res.data.instances.map((ins) => ({
            value: ins.instanceId,
            label: (
              <div key={ins.instanceId}>
                {ins.hostName} <b>{`(${ins.publicIp})`}</b>
              </div>
            ),
            ipAddress: ins.publicIp,
          }))
        );
      })
      .catch((err) => {
        if (
          err?.response?.data?.message === "Invalid credentials" ||
          err?.response?.status === 401
        ) {
          notification.open({ type: "error", content: "Credentials are invalid. Logging out." });
          logOutUser();
        } else {
          notification.open({ type: "error", content: "Unable to fetch instances." });
        }
      });
  };

  const fetchCredRotationPolicy = () => {
    let requestData = {
      pageId: 1,
      pageSize: 100,
      search: "",
      credentialType: "PASSWORD",
      filter: { filterBy: "", value: "" },
      domainId: DOMAIN_ID(),
      orgId: ORG_ID(),
    };
    axios
      .post(`${REACT_APP_API_PAM_URL}/instances/listAllCredentialRotationPolicy`, requestData, {
        headers: { "X-Authorization": AUTH_TOKEN(), withCredentials: true },
      })
      .then((res) => {
        let resData = res?.data?.credentialPolicies;
        const policiesFromApi =
          resData?.map((data) => ({ value: data.policyId, label: data.policyName, ...data })) || [];
        setTemplate([{ value: 0, label: "Default" }, ...policiesFromApi]);
        setCheckPolicyName(resData?.map((data) => data.policyName) || []);
      })
      .catch((err) => {
        if (
          err?.response?.data?.message === "Invalid credentials" ||
          err?.response?.status === 401
        ) {
          notification.open({ type: "error", content: "Credentials are invalid. Logging out." });
          logOutUser();
        } else {
          notification.open({
            type: "error",
            content: "Unable to fetch credential rotation policies.",
          });
        }
      });
  };

  const userType = [
    { value: "LDAP", label: "LDAP" },
    { value: "Local user", label: "Local user" },
  ];
  const rotateEvery = [
    { value: 1, label: "1 day", text: 1 },
    { value: 7, label: "Every week", text: 7 },
    { value: 30, label: "30 days", text: 30 },
  ];

  const handleUserType = (value) => {
    setUserTypeVal(value);
  };

  const handleTemplate = (_, optionData) => {
    if (!optionData || optionData.value === 0) {
      form.resetFields();
      const initialValues = {
        // Re-apply initial values after reset
        userType: "LDAP",
        rotationTime: 1,
        template: 0,
        policyName: "",
        minLength: 0,
        minUppercase: 0,
        minLowercase: 0,
        minDigits: 0,
        minSpecialChars: 0,
        allowStartSpecial: false,
        allowEndSpecial: false,
        specialCharSet: defaultSpecialCharsArray,
        instanceId: [],
        instanceGroupId: [],
      };
      form.setFieldsValue(initialValues);
      setUserTypeVal("LDAP");
      setEndpointLength(false);
      setEndpointGrpLength(false);
      return;
    }

    const templateRotationTimeValue =
      rotateEvery.find((r) => r.text === optionData.rotationInDays)?.value ||
      optionData.rotationInDays;

    let specialCharSetForForm = defaultSpecialCharsArray;
    if (optionData.specialCharSet) {
      if (Array.isArray(optionData.specialCharSet))
        specialCharSetForForm = optionData.specialCharSet;
      else if (typeof optionData.specialCharSet === "string")
        specialCharSetForForm = optionData.specialCharSet.split("");
    }

    let newMinSpecialCharsValue;
    // Interpret existing template data which might have 'enableSpecialCharRules'
    if (
      optionData.hasOwnProperty("enableSpecialCharRules") &&
      optionData.enableSpecialCharRules === false
    ) {
      newMinSpecialCharsValue = 0; // If template explicitly disabled special chars, it means 0.
    } else {
      // Either it was enabled, or enableSpecialCharRules is not in template data (treat as enabled for old templates)
      // Use template's minSpecialChars if available, otherwise default to 1 (if conceptually enabled)
      newMinSpecialCharsValue =
        optionData.minSpecialChars !== undefined ? optionData.minSpecialChars : 1;
      if (optionData.minSpecialChars === 0) newMinSpecialCharsValue = 0; // Ensure if API sent 0, it's 0
    }

    form.setFieldsValue({
      policyName: `${optionData.label} - Copy`,
      userType: optionData.userType,
      rotationTime: templateRotationTimeValue,
      instanceId: optionData.endpoints?.map((i) => i.instanceId) || [],
      instanceGroupId: optionData.endpointGroups?.map((i) => i.groupId) || [],
      minLength: optionData.minLength || 0,
      minUppercase: optionData.minUppercase || 0,
      minLowercase: optionData.minLowercase || 0,
      minDigits: optionData.minDigits || 0,
      minSpecialChars: newMinSpecialCharsValue,
      allowStartSpecial: optionData.allowStartSpecial || false,
      allowEndSpecial: optionData.allowEndSpecial || false,
      specialCharSet: newMinSpecialCharsValue > 0 ? specialCharSetForForm : [],
    });
    setUserTypeVal(optionData.userType);
    setEndpointLength(!!(optionData.endpoints && optionData.endpoints.length > 0));
    setEndpointGrpLength(!!(optionData.endpointGroups && optionData.endpointGroups.length > 0));
    form.validateFields();
  };

  const handlePasswordComplexityChange = (e) => {
    setEnablePasswordComplexity(e.target.checked);
    if (!e.target.checked) {
      // Reset password complexity fields to 0 when disabled
      form.setFieldsValue({
        minLength: 0,
        minUppercase: 0,
        minLowercase: 0,
        minDigits: 0,
        minSpecialChars: 0,
      });
    } else {
      // Set default values to 0 when enabled
      form.setFieldsValue({
        minLength: 0,
        minUppercase: 0,
        minLowercase: 0,
        minDigits: 0,
        minSpecialChars: 0,
      });
    }
  };

  const handleInstall = (values) => {
    setIsLoading(true);

    const payload = {
      template: values.template || 0,
      policyName: values.policyName,
      userType: values.userType,
      rotationTime: values.rotationTime,
      passwordComplexity: enablePasswordComplexity ? 1 : 0,
      minLength: enablePasswordComplexity ? values.minLength || 0 : 8,
      minUppercase: enablePasswordComplexity ? values.minUppercase || 0 : 4,
      minLowercase: enablePasswordComplexity ? values.minLowercase || 0 : 0,
      minDigits: enablePasswordComplexity ? values.minDigits || 0 : 4,
      minSpecialChars: enablePasswordComplexity ? values.minSpecialChars || 0 : 0,
      domainId: DOMAIN_ID(),
      orgId: ORG_ID(),
      instanceGroupId: values.instanceGroupId || [],
      instanceId: values.instanceId || [],
      sessionExpiryTime: 15,
      credentialMedium: "",
      keyFormat: "",
      keyEncryption: "",
      publicKeyFolderPath: "",
      credentialType: "PASSWORD",
    };

    axios
      .post(`${REACT_APP_PAM_API}/instances/addCredentialRotationPolicy`, payload, {
        headers: { withCredentials: true, "X-Authorization": AUTH_TOKEN() },
      })
      .then(() => {
        setIsLoading(false);
        navigate(-1);
        notification.open({ type: "success", content: "Password policy added successfully!" });
      })
      .catch((err) => {
        setIsLoading(false);
        let errorMsg = "Unable to add credential rotation policy.";
        if (err?.response?.data?.message) errorMsg = err.response.data.message;
        notification.open({ type: "error", content: errorMsg });
        if (err?.response?.status === 401) logOutUser();
      });
  };

  const sumOfCharsValidator = ({ getFieldValue }) => ({
    validator(_, value) {
      const minLength = getFieldValue("minLength") || 0;
      const minUppercase = getFieldValue("minUppercase") || 0;
      const minLowercase = getFieldValue("minLowercase") || 0;
      const minDigits = getFieldValue("minDigits") || 0;
      const minSpecial = getFieldValue("minSpecialChars") || 0; // Directly use minSpecialChars value

      const sumOfMinimums = minUppercase + minLowercase + minDigits + minSpecial;

      if (minLength > 0 && sumOfMinimums > minLength) {
        return Promise.reject(
          new Error("Sum of required character types exceeds minimum password length.")
        );
      }
      if (minUppercase > 0 && minLength < minUppercase)
        return Promise.reject(new Error("Min length is less than min uppercase characters."));
      if (minLowercase > 0 && minLength < minLowercase)
        return Promise.reject(new Error("Min length is less than min lowercase characters."));
      if (minDigits > 0 && minLength < minDigits)
        return Promise.reject(new Error("Min length is less than min digits."));
      if (minSpecial > 0 && minLength < minSpecial)
        // Check only if minSpecial is required
        return Promise.reject(new Error("Min length is less than min special characters."));

      return Promise.resolve();
    },
  });

  return (
    <>
      {setNotification}
      <Row className="content-container" style={{ padding: "20px" }}>
        {isOnBoarding && startTour()}
        <Col xs={24} sm={24} md={20} lg={18} xl={16}>
          <Card
            title={<Title level={3}>Create Credential Rotation Policy Password</Title>}
            bordered={false}
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={handleInstall}
              // initialValues are set in useEffect
            >
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item label="Copy from Template" name="template">
                    <Select
                      placeholder="Select a template"
                      onChange={handleTemplate}
                      showSearch
                      optionFilterProp="label"
                      options={template}
                      allowClear
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Divider orientation="left" style={{ borderColor: "#d9d9d9" }}>
                Policy Details
              </Divider>
              <Row gutter={[24, 16]}>
                <Col xs={24} sm={12} md={8} id="onboarding_step1">
                  <Form.Item
                    name="policyName"
                    label="Policy Name"
                    rules={[
                      { required: true, message: "Policy Name is required." },
                      {
                        validator(_, value) {
                          if (checkPolicyName.includes(value))
                            return Promise.reject(new Error("Policy name already exists."));
                          return Promise.resolve();
                        },
                      },
                    ]}
                  >
                    <Input
                      placeholder="Enter a unique policy name"
                      style={{
                        borderRadius: "6px",
                        border: "1px solid #d9d9d9",
                        height: "32px",
                      }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8}>
                  <Form.Item
                    name="userType"
                    label="User Type"
                    rules={[{ required: true, message: "User Type is required." }]}
                  >
                    <Select
                      placeholder="Select user type"
                      onChange={handleUserType}
                      options={userType}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12} md={8} id="onboarding_step3">
                  <Form.Item
                    name="rotationTime"
                    label="Rotate Password Every"
                    rules={[{ required: true, message: "Rotation frequency is required." }]}
                  >
                    <Select
                      placeholder="Select rotation frequency"
                      options={rotateEvery}
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>

              {userTypeVal === "Local user" && (
                <>
                  <Divider orientation="left" style={{ borderColor: "#d9d9d9" }}>
                    Target Endpoints (for Local Users)
                  </Divider>
                  <Row gutter={[24, 16]} id="onboarding_step2">
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="instanceId"
                        label="Endpoints"
                        rules={[
                          {
                            validator: async (_, val) => {
                              if (
                                userTypeVal === "Local user" &&
                                !endpointGrpLength &&
                                (!val || val.length === 0)
                              )
                                return Promise.reject(
                                  new Error("Select at least one Endpoint or Endpoint Group.")
                                );
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Select
                          mode="multiple"
                          placeholder="Select endpoints"
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label?.props?.children[0]?.toLowerCase() ?? "").includes(
                              input.toLowerCase()
                            ) ||
                            (
                              option?.label?.props?.children[1]?.props?.children?.toLowerCase() ??
                              ""
                            ).includes(input.toLowerCase())
                          }
                          options={serverHostName}
                          onChange={(v) => setEndpointLength(v.length >= 1)}
                          disabled={endpointGrpLength}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} sm={12}>
                      <Form.Item
                        name="instanceGroupId"
                        label="Endpoint Groups"
                        rules={[
                          {
                            validator: async (_, val) => {
                              if (
                                userTypeVal === "Local user" &&
                                !endpointLength &&
                                (!val || val.length === 0)
                              )
                                return Promise.reject(
                                  new Error("Select at least one Endpoint or Endpoint Group.")
                                );
                              return Promise.resolve();
                            },
                          },
                        ]}
                      >
                        <Select
                          mode="multiple"
                          placeholder="Select endpoint groups"
                          showSearch
                          optionFilterProp="label"
                          options={epmGroup}
                          onChange={(v) => setEndpointGrpLength(v.length >= 1)}
                          disabled={endpointLength}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </>
              )}

              <Divider orientation="left" style={{ borderColor: "#d9d9d9" }}>
                Password Complexity Rules
              </Divider>
              <Row gutter={[24, 16]}>
                <Col xs={24}>
                  <Form.Item>
                    <Checkbox
                      checked={enablePasswordComplexity}
                      onChange={handlePasswordComplexityChange}
                    >
                      Enable Password Complexity Rules
                    </Checkbox>
                  </Form.Item>
                </Col>
              </Row>
              {enablePasswordComplexity && (
                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="minLength"
                      label="Minimum Password Length"
                      dependencies={[
                        "minUppercase",
                        "minLowercase",
                        "minDigits",
                        "minSpecialChars",
                      ]}
                      rules={[
                        { type: "number", min: 8, message: "Must be at least 8." },
                        sumOfCharsValidator,
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g., 12" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="minUppercase"
                      label="Min Uppercase Letters"
                      dependencies={["minLength"]}
                      rules={[
                        { type: "number", min: 0, message: "Must be 0 or more." },
                        sumOfCharsValidator,
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g., 1" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="minLowercase"
                      label="Min Lowercase Letters"
                      dependencies={["minLength"]}
                      rules={[
                        { type: "number", min: 0, message: "Must be 0 or more." },
                        sumOfCharsValidator,
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g., 1" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="minDigits"
                      label="Min Numeric Digits"
                      dependencies={["minLength"]}
                      rules={[
                        { type: "number", min: 0, message: "Must be 0 or more." },
                        sumOfCharsValidator,
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g., 1" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Form.Item
                      name="minSpecialChars"
                      label="Min Special Characters"
                      dependencies={["minLength"]}
                      rules={[
                        {
                          type: "number",
                          min: 0,
                          message: "Must be 0 or more.",
                        },
                        sumOfCharsValidator,
                      ]}
                    >
                      <InputNumber style={{ width: "100%" }} placeholder="e.g., 1" />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Divider style={{ borderColor: "#d9d9d9" }} />
              <Row justify="end" style={{ marginTop: "24px" }}>
                <Col>
                  <Space>
                    <Button onClick={() => navigate(-1)}>Cancel</Button>
                    <Button
                      type="primary"
                      htmlType="submit"
                      loading={isLoading}
                      id="onboarding_step4"
                    >
                      Save Policy
                    </Button>
                  </Space>
                </Col>
              </Row>
            </Form>
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default AddPasswordPolicy;
